import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getTotalPainReports } from "../../../api/getOverviewData";
import SmallOverviewWithIcon from "./SmallOverviewWithIcon";
import { Skeleton } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import { getTheme } from "../../../theme";

type TotalPainReportsProps = { projectCodes: string[]; height: number };

export type TotalPainReportsRef = {
  isReady: () => boolean;
  getData: () => number[];
};

const TotalPainReports = forwardRef<TotalPainReportsRef, TotalPainReportsProps>(
  function TotalPainReports({ projectCodes, height }, ref) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<number[]>([]);
    const theme = getTheme();

    useImperativeHandle(ref, () => ({
      isReady: () => isLoading,
      getData: () => data,
    }));

    useEffect(() => {
      if (projectCodes.length === 0) return;
      setIsLoading(true);
      let isMounted = true;

      let controller = new AbortController();

      const fetchPainReportsPerDay = async () => {
        try {
          const data = await getTotalPainReports(projectCodes, controller);
          if (isMounted) setData(data);
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.log("Fetch aborted");
          } else {
            console.error("Fetch error:", error);
          }
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };

      fetchPainReportsPerDay();

      return () => {
        isMounted = false;
        controller.abort(); // Abort previous request when effect runs again
      };
    }, [projectCodes]);

    return data.length > 0 && isLoading === false ? (
      <SmallOverviewWithIcon
        height={height}
        titleMain="Total Pain Reports"
        titleSecondary="today"
        contentMain={data[0]}
        contentSecondary={data[1]}
        icon={BoltIcon}
        iconColor={theme.palette.secondaryChart}
      />
    ) : (
      <Skeleton
        sx={{
          borderRadius: "5px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        animation="pulse"
        variant="rectangular"
        width={"100%"}
        height={height}
      />
    );
  }
);

export { TotalPainReports };
